import React, { useRef, useState } from "react";
import { useParams, useNavigate, useLoaderData } from "react-router-dom";
import { useAuthContext } from "../../hooks/useAuthContext";
import { TabMenu } from "primereact/tabmenu";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import MeaPage from "../../components/MeaPage";
import MeaPageTitle from "../../components/MeaPageTitle";
import MeaLoadingContent from "../../components/MeaLoadingContent";
import MeaDetailField from "../../components/MeaDetailField";
import MeaDetailCard from "../../components/MeaDetailCard";
import classesAPI from "../../services/classes";
import trainingsAPI from "../../services/trainings";
import scheduleAPI from "../../services/schedule";
import accountsAPI from "../../services/accounts";
import StudentsListComponent from "../trainings/students/components/StudentsListComponent";
import ScheduleListComponent from "../trainings/schedule/components/ScheduleListComponent";
import SessionsListComponent from "../trainings/sessions/components/SessionsListComponent";

export function TeacherClasseDetailPage() {
  const loaderData = useLoaderData();
  const params = useParams();
  const navigate = useNavigate();
  const { auth, currentMembership, updateCurrentMembershipPreferences } = useAuthContext();

  const schoolId = loaderData?.school?.id || params.schoolId || currentMembership?.school?.id;
  const trainingId = params.trainingId;
  const classeId = params.classeId;

  const [activeItem, setActiveItem] = useState(0);
  const listRef = useRef();

  const classeQuery = classesAPI.GetOne(
    schoolId,
    trainingId,
    auth?.access_token,
    classeId
  );
  const classe = classeQuery?.data?.data;

  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, auth?.access_token);
  const training = trainingQuery?.data?.data;

  const trainingsQuery = trainingsAPI.useGetList(
    schoolId,
    auth?.access_token,
    1,
    100,
    ""
  );
  const trainings = trainingsQuery?.data?.data || [];

  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    auth?.access_token,
    1,
    50,
    { classe_id: classeId }
  );
  const scheduleItems = scheduleQuery?.data?.data || [];

  const slotsList = [];
  const seenSlots = new Set();
  scheduleItems.forEach((item) => {
    if (item.slot && !seenSlots.has(item.slot.id)) {
      seenSlots.add(item.slot.id);
      const timeStr =
        item.slot.start_hour && item.slot.end_hour
          ? ` (${item.slot.start_hour} - ${item.slot.end_hour})`
          : "";
      slotsList.push(`${item.slot.name}${timeStr}`);
    }
  });

  const classroomsList = [];
  const seenClassrooms = new Set();
  scheduleItems.forEach((item) => {
    if (item.classroom && !seenClassrooms.has(item.classroom.id)) {
      seenClassrooms.add(item.classroom.id);
      const locStr = item.classroom.location ? ` (${item.classroom.location})` : "";
      classroomsList.push(`${item.classroom.name}${locStr}`);
    }
  });

  const slotDisplay = slotsList.length > 0 ? slotsList.join(", ") : "-";
  const classroomDisplay = classroomsList.length > 0 ? classroomsList.join(", ") : "-";

  const getStatusLabel = (status) => {
    switch (status) {
      case "OPEN":
        return "Ouvert";
      case "DRAFT":
        return "Brouillon";
      case "SUSPENDED":
        return "Suspendu";
      case "ARCHIVED":
        return "Archivé";
      default:
        return status;
    }
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center">
        <span className="font-medium text-color">{option.name}</span>
        <span
          className={`text-xs px-2 py-1 border-round font-semibold ml-2 ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const valueTemplate = (option, props) => {
    if (!option) {
      const currentReg = trainings.find((t) => t.id === trainingId) || training;
      if (!currentReg) {
        return <span>{props.placeholder}</span>;
      }
      option = currentReg;
    }
    return (
      <div className="flex align-items-center">
        <span className="font-bold text-color mr-2">{option.name}</span>
        <span
          className={`text-xs px-2 py-0.5 border-round font-semibold ${
            option.status === "OPEN"
              ? "bg-green-100 text-green-800"
              : option.status === "DRAFT"
              ? "bg-blue-100 text-blue-800"
              : option.status === "SUSPENDED"
              ? "bg-red-100 text-red-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {getStatusLabel(option.status)}
        </span>
      </div>
    );
  };

  const handleTrainingChange = async (e) => {
    const newTrainingId = e.value;
    if (newTrainingId && newTrainingId !== trainingId) {
      try {
        const updatedPrefs = await accountsAPI.UpdateMemberPreferences(
          auth?.access_token,
          currentMembership?.id,
          newTrainingId
        );
        if (updatedPrefs) {
          updateCurrentMembershipPreferences(updatedPrefs);
        } else {
          const fallbackPrefs = currentMembership?.preferences
            ? { ...currentMembership.preferences, last_visited_training: newTrainingId }
            : { last_visited_training: newTrainingId };
          updateCurrentMembershipPreferences(fallbackPrefs);
        }
      } catch (err) {
        console.error("Failed to update last visited preference", err);
      }
      navigate(`/school/${schoolId}/teacher/classes/${newTrainingId}`);
    }
  };

  const sectionOptions = [
    { label: "Mes séances de cours", value: "sessions" },
    { label: "Mes classes", value: "classes" },
    { label: "Avis de passage", value: "avis-passage" },
    { label: "Agenda", value: "agenda" },
  ];

  const handleSectionChange = (e) => {
    const newSection = e.value;
    if (!newSection || newSection === "classes") {
      return;
    }

    if (newSection === "sessions") {
      navigate(`/school/${schoolId}/teacher/sessions/${trainingId}`);
      return;
    }

    if (newSection === "agenda") {
      navigate(`/school/${schoolId}/teacher/agenda/${trainingId}`);
      return;
    }

    navigate(`/school/${schoolId}/teacher/avis-passage/${trainingId}`);
  };

  const customWorkspaceBar = (
    <div className="w-full p-breadcrumb border-bottom-1 surface-border py-2 px-3 flex align-items-center flex-wrap gap-2">
      <div className="flex align-items-center gap-2">
        <i className="pi pi-th-large text-teal-600 text-sm"></i>
        <Dropdown
          value="classes"
          options={sectionOptions}
          onChange={handleSectionChange}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <div className="flex align-items-center gap-2">
        <i className="pi pi-bookmark text-indigo-500 text-sm"></i>
        <Dropdown
          value={trainingId}
          options={trainings}
          optionLabel="name"
          optionValue="id"
          onChange={handleTrainingChange}
          placeholder={training?.name || "Sélectionner une formation"}
          className="dropdown-breadcrumb font-bold text-sm sm:text-base"
          style={{ width: "auto" }}
          itemTemplate={itemTemplate}
          valueTemplate={valueTemplate}
        />
      </div>
      <span className="text-300 font-light text-base sm:text-lg">/</span>
      <span className="text-900 font-semibold text-sm sm:text-base px-2">
        {classe?.name || "Classe"}
      </span>
    </div>
  );

  const tabItems = [
    { label: "Élèves", icon: "pi pi-users" },
    { label: "Programme de la semaine", icon: "pi pi-clock" },
    { label: "Séances de cours", icon: "pi pi-calendar" },
  ];

  const handlePrintForTeachers = () => {
    navigate(`/school/${schoolId}/teacher/classes/${trainingId}/classe/${classeId}/print-teachers`);
  };

  const customActions = (
    <div className="flex gap-2">
      <Button
        label="Imprimer"
        icon="pi pi-print"
        className="mr-2"
        onClick={handlePrintForTeachers}
      />
    </div>
  );

  return (
    <MeaPage
      showBackButton={true}
      onBackButtonClick={() => navigate(`/school/${schoolId}/teacher/classes/${trainingId}`)}
      title={<MeaPageTitle title={classe?.name} />}
      breadcrumb={customWorkspaceBar}
      customActions={customActions}
    >
      <MeaLoadingContent objectQuery={classeQuery}>
        <MeaDetailCard>
          <MeaDetailField field="Nom de la classe" value={classe?.name} />
          <MeaDetailField field="Niveau" value={classe?.level?.name || "-"} />
          <MeaDetailField field="Créneau" value={slotDisplay} />
          <MeaDetailField field="Salle" value={classroomDisplay} />
          <MeaDetailField field="Capacité" value={classe?.max_capacity || "-"} />
          <MeaDetailField field="Nombre d'élèves" value={classe?.total_students || "0"} />
          <MeaDetailField
            field="Référent(e)"
            value={
              classe?.referent
                ? classe.referent.name
                  ? classe.referent.name
                  : `${classe.referent.lastname?.toUpperCase() || ""} ${classe.referent.firstname || ""}`.trim()
                : "-"
            }
          />
          <MeaDetailField field="Présentation" value={classe?.presentation || "-"} />
        </MeaDetailCard>

        <div className="mt-5">
          <TabMenu
            model={tabItems}
            activeIndex={activeItem}
            onTabChange={(e) => setActiveItem(e.index)}
          />

          {activeItem === 0 && (
            <MeaLoadingContent objectQuery={classeQuery}>
              <StudentsListComponent
                ref={listRef}
                schoolId={schoolId}
                trainingId={trainingId}
                filter={{ classe_id: classe?.id }}
                familyColumnLabel="Fiche élève"
                familyColumnIcon="pi pi-id-card"
                familyColumnTooltip="Voir la fiche de l'élève"
                familyColumnTooltipOptions={{ position: "top" }}
                showSlotChangeColumn={false}
                showAddButton={false}
                showFilterButton={false}
                disableNavigation={true}
                openStudentSheetOnNameClick={true}
                selectedFields={{
                  level: false,
                  classe: false,
                  passing: false,
                  observation: false,
                  status: true,
                  allergies: true,
                  health_issues: true,
                  authorized_take_photo: true,
                  authorized_back_alone: true,
                }}
              />
            </MeaLoadingContent>
          )}

          {activeItem === 1 && (
            <MeaLoadingContent objectQuery={classeQuery}>
              <ScheduleListComponent
                ref={listRef}
                schoolId={schoolId}
                trainingId={trainingId}
                filter={{ classe_id: classe?.id }}
                showAddButton={false}
                showFilterButton={false}
                disableNavigation={true}
                selectedFields={{
                  classe: false,
                }}
              />
            </MeaLoadingContent>
          )}

          {activeItem === 2 && (
            <MeaLoadingContent objectQuery={classeQuery}>
              <SessionsListComponent
                ref={listRef}
                schoolId={schoolId}
                trainingId={trainingId}
                showAddButton={false}
                showFilterButton={false}
                showAllSessions={true}
                disableNavigation={true}
                filter={{
                  classe_id: classe?.id,
                  start_date: training?.start_courses_date,
                  end_date: training?.end_courses_date,
                }}
                selectedFields={{
                  presence: true,
                  level: false,
                  classe: false,
                }}
              />
            </MeaLoadingContent>
          )}
        </div>
      </MeaLoadingContent>
    </MeaPage>
  );
}

export default TeacherClasseDetailPage;
